<template>
  <section>
    <summary-block
      :properties="financingSummary"
      :is-loading="financingIsLoading"
      :data-qa="modalId + '-block'"
      @show-in-pdf="changeShowInPdf"
      @show-config-modal="showConfigModal"
    />

    <modal
      v-if="showFinancialServiceConfig"
      :id="modalId"
      :title="retrieveModalText('title')"
      @close="showConfigModal(false)"
    >
      <lease-form
        :financial-params="computedFinancialParams"
        :show-apply-for-lease="showInPdf && isPrivateLease"
        :product-types="productTypes"
        :is-read-only="isReadOnly"
        :dictionary-folder="productTypeDictionaryFolder"
        :is-loading="financingIsLoading"
        :quotation-id="quotationId"
        :lease-type="leaseType"
        :vwpfs-lease-data="latestVwpfsCall"
        @update-financing="handleUpdateFinancing"
        @update-discount="updateDiscount"
        @update-multiple-discounts="updateMultipleDiscounts"
        @submit="showConfigModal(false)"
        @apply-for-lease="applyForLease"
      />
    </modal>

    <confirmation-prompt
      id="leaseApply"
      :title="promtTitle"
      :info-text="leaseInfoText"
      :confirm-button-text="i18n.t('/common/confirmation')"
      :show="showLeaseApply"
      :align-buttons="'center'"
      :cancel-button-show="false"
      @confirm="openLeaseLink()"
      @close="openLeasePrompt(false)"
    >
    </confirmation-prompt>
  </section>
</template>

<script>
import ConfirmationPrompt from '@/components/generic/presentational/molecules/confirmationPrompt/ConfirmationPrompt.vue'
import Modal from '@/components/generic/presentational/molecules/modal/Modal.vue'
import SummaryBlock from '@/components/generic/container/FinancialServices/SummaryBlock.vue'
import FinancialServices from '@/api/server/FinancialServices'
import { mapActions, mapGetters, mapState } from 'vuex'
import FinancialServiceBlock from '@/mixins/financialServices/financialServiceBlock'
import LeaseForm from '@/components/generic/container/FinancialServices/Lease/LeaseForm.vue'
import { EventBus } from '@/plugins/event-bus'
import AddressApi from '@/api/server/Address'
import CustomerSearchHelper from '@/helpers/CustomerSearchHelper'
import CountryHelper from '@/helpers/CountryHelper'
import CurrencyHelper from '@/helpers/currencyHelper'
import discountType from '@/helpers/enums/discountType'
import constants from '@/utils/constants'

export default {
  components: {
    ConfirmationPrompt,
    Modal,
    SummaryBlock,
    LeaseForm,
  },
  mixins: [FinancialServiceBlock],
  props: {
    leaseType: {
      type: String,
      required: true,
    },
  },
  emits: [],
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      quotationId: null,
      leaseLink: null,
      showLeaseApply: false,
      leaseInfoText: '',
      // Properties from modal
      financialParams: {
        // Default params
        duration: null,
        yearlyKm: null,
        ownRiskLevel: null,
        reliefVehicleProduct: null,
        tireProduct: null,
        province: null,

        leasePrice: null,
        excessKmPriceCt: '',
        lessKmPriceCt: '',
        errors: null,
        noPrivateLeaseAvailable: false,

        downPayment: 0,
        prePayment: 0,
        rateAmount: null,
        ratePrePaymentMonthly: null,
        insuranceProduct: 'WCS',
        fuelCardProduct: 'NONE',

        multipleDiscounts: [],
        collectivityCode: null,
      },

      leaseTypes: constants.leaseTypes,
    }
  },
  computed: {
    ...mapGetters({
      isReadOnly: 'quote/quoteStatusIsSalesOrder',
      latestVwpfsPrivateLeaseCall: 'quote/latestVwpfsPrivateLeaseCall',
      latestVwpfsOperationalLeaseCall: 'quote/latestVwpfsOperationalLeaseCall',
      customerZipcode: 'quote/customerZipcode',
      customerCountryCode: 'quote/customerCountryCode',
      dealerZipcode: 'quote/dealerZipcode',
      totalPrice: 'quote/totalPrice',
      tempLatestVwpfsCalls: 'quote/tempLatestVwpfsCalls',
      configurationStatus: 'configuration/status',
      showVwpfsFinancePdf: 'quote/showVwpfsFinancePdf',
      showVwpfsPrivateLeasePdf: 'quote/showVwpfsPrivateLeasePdf',
      showVwpfsInsurancePdf: 'quote/showVwpfsInsurancePdf',
      showVwpfsOperationalLeasePdf: 'quote/showVwpfsOperationalLeasePdf',
      contractor: 'quote/printOptions/contractor',
      contactInfoArray: 'quote/printOptions/contactInfo',
    }),
    ...mapState('user', ['dealerId', 'dealerSettings']),
    leaseProductType() {
      return Object.freeze({
        privateLease: 'PRIVATE_LEASE',
        operationalLease: 'OPERATIONAL_LEASE',
      })
    },
    isPrivateLease() {
      return this.leaseType === this.leaseTypes.PRIVATE_LEASE
    },
    isOperationalLease() {
      return this.leaseType === this.leaseTypes.OPERATIONAL_LEASE
    },
    labelPrefix() {
      return this.isPrivateLease
        ? '/financialservices/lease'
        : '/financialservices/operationallease'
    },
    modalId() {
      return this.isPrivateLease ? 'private-lease' : 'operational-lease'
    },
    latestVwpfsCall() {
      return this.isPrivateLease
        ? this.latestVwpfsPrivateLeaseCall
        : this.latestVwpfsOperationalLeaseCall
    },
    tempLatestVwpfsCall() {
      return this.isPrivateLease
        ? this.tempLatestVwpfsCalls?.LatestVwpfsPrivateLeaseCall
        : this.tempLatestVwpfsCalls?.LatestVwpfsOperationalLeaseCall
    },
    options() {
      return {
        duration: this.isPrivateLease ? [24, 30, 36, 42, 48, 54, 60] : [12, 24, 36, 48, 60],
        yearlyKm: this.isPrivateLease
          ? [5000, 10000, 15000, 20000, 25000, 30000, 35000, 40000, 45000, 50000]
          : [10000, 15000, 20000, 25000, 30000, 35000, 40000, 45000],
        tires: ['W', 'Z', 'A'],
        reliefVehicle: this.isPrivateLease ? ['100185'] : ['NONE', 'N24', 'DIR'],
        insuranceProduct: ['NONE', 'WCS'],
        fuelCardProduct: ['NONE', 'LEU', 'LNL', 'TEU', 'TNL', 'CUE', 'CNL'],
      }
    },
    promtTitle() {
      const sectionText = this.retrieveText('sectiontitle')
      const applyText = this.i18n.t('/financialservices/financing/modal/silvaprompt/title')

      return `${sectionText} ${applyText.toLowerCase()}`
    },
    productTypes() {
      return {
        lease: [
          this.isPrivateLease
            ? {
                name: this.retrieveModalText('privateleaselabel'),
                value: this.leaseProductType.privateLease,
              }
            : {
                name: this.retrieveModalText('operationalleaselabel'),
                value: this.leaseProductType.operationalLease,
              },
        ],
        province: this.isPrivateLease
          ? [
              { name: this.retrieveModalText('unknownlabel'), value: null },
              ...CountryHelper.provinceList(),
            ]
          : [CountryHelper.provinceList().filter((province) => province.value == 'UT')],
        duration: this.options.duration.map((x) => ({
          name: `${x} ${this.retrieveModalText('durationpostfix')}`,
          value: x,
        })),
        yearlyKm: this.options.yearlyKm.map((x) => ({
          name: this.i18n.n(x),
          value: x,
        })),
        tires: this.options.tires.map((x) => ({
          name: this.retrieveInfoText(`tires/label/${x}`),
          summary: this.retrieveInfoText(`tires/summary/${x}`),
          value: x,
        })),
        reliefVehicle: this.options.reliefVehicle.map((x) => ({
          name: this.retrieveInfoText(`reliefvehicle/label/${x}`),
          summary: this.retrieveInfoText(`reliefvehicle/summary/${x}`),
          value: x,
        })),
        ownRiskLevel: this.isPrivateLease
          ? constants.PRIVATE_LEASE_OWN_RISK_LEVELS
          : constants.OPERATIONAL_LEASE_OWN_RISK_LEVELS,
        insuranceProduct: this.isPrivateLease
          ? null
          : this.options.insuranceProduct.map((x) => ({
              name: this.retrieveInfoText(`insuranceProduct/label/${x}`),
              summary: this.retrieveInfoText(`insuranceProduct/summary/${x}`),
              value: x,
            })),
        fuelCardProduct: this.isPrivateLease
          ? null
          : this.options.fuelCardProduct.map((x) => ({
              name: this.retrieveInfoText(`fuelCardProduct/label/${x}`),
              summary: this.retrieveInfoText(`fuelCardProduct/summary/${x}`),
              value: x,
            })),
      }
    },
    computedFinancialParams() {
      return {
        leaseType: this.isPrivateLease
          ? this.leaseProductType.privateLease
          : this.leaseProductType.operationalLease,
        ...this.financialParams,
        configurationInvalid: this.configurationStatus ? !this.configurationStatus.IsValid : false,
      }
    },
    summaryLineParams() {
      let params = {
        duration: this.financialParams.duration,
        yearlyKm: this.i18n.n(this.financialParams.yearlyKm),
        ownRiskLevel: this.i18n.n(
          parseInt(
            this.getSelectedProduct('ownRiskLevel', this.financialParams.ownRiskLevel)?.name
          ),
          'currencyRounded'
        ),
        reliefVehicleProduct: this.getSelectedProduct(
          'reliefVehicle',
          this.financialParams.reliefVehicleProduct
        )?.summary,
        tireProduct: this.getSelectedProduct('tires', this.financialParams.tireProduct)?.summary,
        downPayment: this.i18n.n(this.financialParams.downPayment, 'currencyRounded'),
        prePayment: this.i18n.n(this.financialParams.prePayment, 'currencyRounded'),
      }

      if (this.isOperationalLease) {
        params.noInsuranceOrOwnRiskLevel =
          this.financialParams.insuranceProduct == 'WCS'
            ? 'eigen risico: ' + params.ownRiskLevel
            : 'verzekering: geen verzekering'

        params.fuelCardProduct = this.getSelectedProduct(
          'fuelCardProduct',
          this.financialParams.fuelCardProduct
        ).summary
      }

      return params
    },
    financingSummary() {
      return {
        title: this.retrieveText('sectiontitle'),
        summaryLine: this.retrieveText('summaryline', this.summaryLineParams),
        rateTotal: this.financialParams.leasePrice
          ? CurrencyHelper.currencyRounded(this.financialParams.leasePrice)
          : '-',
        infoModalTitle: this.retrieveText('summaryinfotitle'),
        infoModalContent: this.retrieveInfoText('summaryinfotext'),
        showInPdfCheckboxLabel: this.retrieveText('showVwpfsFinancePdf Label'),
        openModalButtonLabel: this.retrieveText('openmodalbutton'),
        isReadOnly: this.isReadOnly,
        showInPdf: this.showInPdf,
      }
    },
    productTypeDictionaryFolder() {
      return this.isPrivateLease ? 'privatelease' : ''
    },
    setLatestCallName() {
      return this.isPrivateLease
        ? 'quote/setLatestVwpfsPrivateLeaseCall'
        : 'quote/setLatestVwpfsOperationalLeaseCall'
    },
  },
  watch: {
    showFinancialServiceConfig(val) {
      if (!val) {
        this.quotationId = null
      }
    },
  },
  created() {
    this.initDefaultLeaseParams()
  },
  mounted() {
    EventBus.$on(
      this.isPrivateLease ? 'set-private-lease-data' : 'set-operational-lease-data',
      () => {
        if (this.tempLatestVwpfsCall) {
          this.$store.dispatch(this.setLatestCallName, this.tempLatestVwpfsCall).then(() => {
            this.$store.commit(
              'quote/clearTempLatestVwpfsCalls',
              this.isPrivateLease
                ? 'LatestVwpfsPrivateLeaseCall'
                : 'LatestVwpfsOperationalLeaseCall'
            )
            this.requestFinancing()
          })
        } else if (!this.financialParams.noPrivateLeaseAvailable)
          this.$store.dispatch(this.setLatestCallName, {
            response: {
              pricingData: {
                calculatedProduct: {
                  rate: {
                    amount: this.financialParams.leasePrice,
                  },
                  excessLessKms: {
                    excessKmPriceCt: this.financialParams.excessKmPriceCt,
                    lessKmPriceCt: this.financialParams.lessKmPriceCt,
                  },
                },
              },
            },
            responseCode: 200,
          })
      }
    )
    EventBus.$on('configuration-updated', this.requestFinancing)
    EventBus.$on('quote/deliveryDateUpdated', this.onDeliveryDateUpdated)
    EventBus.$on('quote/customerUpdated', this.updateProvinceFromCustomer)
    EventBus.$on('saveLease', this.saveLease)
  },
  unmounted() {
    EventBus.$off(this.isPrivateLease ? 'set-private-lease-data' : 'set-operational-lease-data')
    EventBus.$off('configuration-updated', this.requestFinancing)
    EventBus.$off('quote/deliveryDateUpdated', this.onDeliveryDateUpdated)
    EventBus.$off('quote/customerUpdated', this.updateProvinceFromCustomer)
    EventBus.$off('saveLease', this.saveLease)
  },
  methods: {
    ...mapActions({
      setPlQuotationId: 'quote/setPlQuotationId',
    }),
    initDefaultLeaseParams() {
      // Initialise data props
      this.financialParams.duration = this.isPrivateLease ? null : 48
      this.financialParams.yearlyKm = this.isPrivateLease ? null : 20000
      this.financialParams.reliefVehicleProduct = this.isPrivateLease ? null : 'N24'
      this.financialParams.ownRiskLevel = this.isPrivateLease ? null : 'C135'
      if (this.isOperationalLease) {
        this.financialParams.province = 'UT'
      }
    },
    onDeliveryDateUpdated() {
      if (this.isPrivateLease) {
        this.requestFinancing()
      }
    },
    async requestFinancing() {
      if (this.computedFinancialParams.configurationInvalid) {
        // If configuration is invalid don't make PL call
        return
      }
      this.setFinancingIsLoading(true)
      const request = this.getLeaseRequestParams()

      this.financialParams.errors = null

      let leaseCallFunction = this.isPrivateLease
        ? FinancialServices.setPrivateLease
        : FinancialServices.setOperationalLease

      const response = await leaseCallFunction(request).catch((error) => {
        this.financialParams.leasePrice = null
        this.$store.dispatch(this.setLatestCallName, {
          responseCode: error.response?.status,
        })

        this.parseMessages(error.response?.data?.messages, error.response?.status)

        this.setFinancingIsLoading(false)
        return null
      })
      // 404 error
      if (!response) {
        return
      }
      this.financialParams.noPrivateLeaseAvailable = false
      if (
        !request.plCalculationParameters.duration &&
        !request.plCalculationParameters.yearlyKm &&
        this.isPrivateLease
      ) {
        this.restoreDefaultParamsFromResponse(response)
      }
      this.setLeaseProductPriceFromResponse(response)
      this.parseMessages(response?.messages, 200)

      this.$store.dispatch(this.setLatestCallName, {
        response: { ...response },
        ...request.plCalculationParameters,
        ...request.leaseProductDiscounts,
        responseCode: 200,
      })

      this.setFinancingIsLoading(false)
    },
    restoreDefaultParamsFromResponse(response) {
      let responseCalculationParams = response?.leaseProductData?.calculationParameters?.find(
        (p) => p.input === 'USED_PARAMETERS_LABEL'
      )
      if (!responseCalculationParams) return

      this.financialParams.duration = responseCalculationParams.duration?.match(/(\d+)/)?.[0]
      this.financialParams.yearlyKm = responseCalculationParams.yearlyKm?.match(/(\d+)/g)?.join('')

      let intOwnRiskLevel = responseCalculationParams.ownRiskLevel?.match(/(\d+)/)?.[0]
      this.financialParams.ownRiskLevel = this.getSelectedProductByName(
        'ownRiskLevel',
        intOwnRiskLevel
      )?.value

      this.financialParams.reliefVehicleProduct = this.getSelectedProductByName(
        'reliefVehicle',
        responseCalculationParams.reliefVehicleProduct
      )?.value

      this.financialParams.tireProduct = this.getSelectedProductByName(
        'tires',
        responseCalculationParams.tireProduct
      )?.value
    },
    parseMessages(messages, status) {
      try {
        // Handle messages array directly if it's already an array
        if (Array.isArray(messages)) {
          this.financialParams.errors = messages

          let errorPrefix = this.isPrivateLease ? 'Private Lease: ' : 'Operational Lease: '
          // Emit notification for each message
          messages.forEach((msg) => {
            if (msg.code !== 'VehicleTypeNotSupported' && msg.message) {
              window.VUE_CLI_EXPORTS.plugins.EventBus.$emit(
                'notification-warning',
                errorPrefix + msg.message
              )
            }
          })
        } else if (typeof messages === 'string') {
          // Try to parse if it's a JSON string
          const parsed = JSON.parse(messages)
          this.financialParams.errors = parsed?.errors || parsed
        } else {
          this.financialParams.errors = messages
        }

        if (status === 404) {
          if (this.financialParams.errors?.find((x) => x.code === 'VehicleMatchError')) {
            this.financialParams.noPrivateLeaseAvailable = true
            this.financialParams.errors = null
          }
        }
      } catch (e) {
        this.financialParams.errors = messages
      }
    },
    setLeaseProductPriceFromResponse(response) {
      const calculatedProduct = response?.pricingData?.calculatedProduct

      this.financialParams.leasePrice =
        calculatedProduct?.rate?.amountAfterPrePayment ?? calculatedProduct?.rate?.amount

      this.financialParams.rateAmount = calculatedProduct?.rate?.amount ?? null
      this.financialParams.ratePrePaymentMonthly = calculatedProduct?.rate?.prePayment ?? null

      let floatExcessKmPriceCt = parseFloat(calculatedProduct?.excessLessKms?.excessKmPriceCt)
      if (isNaN(floatExcessKmPriceCt)) {
        this.financialParams.excessKmPriceCt = 0
      } else {
        this.financialParams.excessKmPriceCt = calculatedProduct?.excessLessKms?.excessKmPriceCt
      }

      let floatLessKmPriceCt = parseFloat(calculatedProduct?.excessLessKms?.lessKmPriceCt)
      if (isNaN(floatLessKmPriceCt)) {
        this.financialParams.lessKmPriceCt = 0
      } else {
        this.financialParams.lessKmPriceCt = calculatedProduct?.excessLessKms?.lessKmPriceCt
      }
    },
    getLeaseRequestParams() {
      const request = {
        quoteId: this.quote?.QuoteId,
        plCalculationParameters: {
          duration: this.financialParams.duration,
          yearlyKm: this.financialParams.yearlyKm,
          ownRiskLevel: this.financialParams.ownRiskLevel,
          tireProduct: this.financialParams.tireProduct,
          reliefVehicleProduct: this.financialParams.reliefVehicleProduct,
          province: this.financialParams.province,
          collectivityCode: this.financialParams.collectivityCode,
          downPayment: this.isOperationalLease ? this.financialParams.downPayment : null,
          prePayment: this.isPrivateLease ? this.financialParams.prePayment : null,
        },
        leaseProductDiscounts: {
          multipleDiscounts: this.financialParams.multipleDiscounts,
        },
      }

      if (this.isOperationalLease) {
        request.plCalculationParameters.insuranceProduct = this.financialParams.insuranceProduct
        request.plCalculationParameters.fuelCardProduct = this.financialParams.fuelCardProduct
      }

      return request
    },
    async initFinancing() {
      this.showInPdf = this.isPrivateLease
        ? this.showVwpfsPrivateLeasePdf
        : this.showVwpfsOperationalLeasePdf
      if (!!this.latestVwpfsCall) {
        this.setFinancingIsLoading(true)

        const latestVwpfsLeaseCallData = this.latestVwpfsCall

        this.financialParams.duration = latestVwpfsLeaseCallData.duration
        this.financialParams.yearlyKm = latestVwpfsLeaseCallData.yearlyKm
        this.financialParams.ownRiskLevel = latestVwpfsLeaseCallData.ownRiskLevel
        this.financialParams.reliefVehicleProduct = this.isPrivateLease
          ? this.getValidPrivateLeaseReliefVehicleProduct(
              latestVwpfsLeaseCallData.reliefVehicleProduct
            )
          : latestVwpfsLeaseCallData.reliefVehicleProduct
        this.financialParams.tireProduct = latestVwpfsLeaseCallData.tireProduct
        this.financialParams.province = latestVwpfsLeaseCallData.province

        this.financialParams.multipleDiscounts = latestVwpfsLeaseCallData.multipleDiscounts || []

        this.financialParams.collectivityCode = latestVwpfsLeaseCallData.collectivityCode

        this.financialParams.downPayment = latestVwpfsLeaseCallData.downPayment
        this.financialParams.prePayment = latestVwpfsLeaseCallData.prePayment

        this.financialParams.rateAmount = null
        this.financialParams.ratePrePaymentMonthly = null

        if (this.isOperationalLease) {
          this.financialParams.insuranceProduct = latestVwpfsLeaseCallData.insuranceProduct
          this.financialParams.fuelCardProduct = latestVwpfsLeaseCallData.fuelCardProduct
        }

        if (!this.isReadOnly) {
          this.requestFinancing()
        } else {
          this.setLeaseProductPriceFromResponse(latestVwpfsLeaseCallData.response)

          this.financialParams.noPrivateLeaseAvailable =
            latestVwpfsLeaseCallData.responseCode === 404
        }
        this.setFinancingIsLoading(false)
      } else {
        if (this.isPrivateLease) {
          //do private lease call
          await this.updateProvinceFromCustomer()
        } else {
          //do operational lease call
          await this.requestFinancing()
        }
      }
      // Make private lease call on any price change, prevent double update when config is changed
      this.$watch('totalPrice.Excl', () => {
        if (!this.configurationIsUpdating) {
          this.requestFinancing()
        }
      })
    },
    /**
     **
     * Returns the relief vehicle product for private lease.
     * Falls back to '100185' (Per direct) if the stored value is no longer valid,
     * e.g. when a quote was saved with '100182' (Na 24 uur) which has since been
     * removed from the private lease options (CST-4425).
     */
    getValidPrivateLeaseReliefVehicleProduct(storedValue) {
      return this.options.reliefVehicle.includes(storedValue) ? storedValue : '100185'
    },
    async changeShowInPdf(showInPdf) {
      this.showInPdf = showInPdf

      if (this.isPrivateLease) {
        await this.$store.dispatch('quote/setShowVwpfsPrivateLeasePdf', showInPdf)
      } else {
        await this.$store.dispatch('quote/setShowVwpfsOperationalLeasePdf', showInPdf)
      }

      const pdfOptions = {
        quoteId: this.quote?.QuoteId,
        printContractorOnOrder: this.contractor,
        printContactInfoOnPdf: this.contactInfoArray,
        showVwpfsFinancePdf: this.showVwpfsFinancePdf,
        showVwpfsPrivateLeasePdf: this.showVwpfsPrivateLeasePdf,
        showVwpfsInsurancePdf: this.showVwpfsInsurancePdf,
        showVwpfsOperationalLeasePdf: this.showVwpfsOperationalLeasePdf,
      }

      await this.$store.dispatch('quote/savePrintOptions', pdfOptions)
    },
    async getProvinceFromPostcode(postcode) {
      return (
        await AddressApi.getProvinceByZipcode(CustomerSearchHelper.parsePostcodeNumbers(postcode))
      )?.ProvinceAbbreviation
    },
    async updateProvinceFromCustomer() {
      if (!this.isPrivateLease) return
      let tryDealerPostcode = false
      let zipcode = this.customerZipcode
      if (this.customerCountryCode !== 'NL' || !zipcode) {
        // If no customer zipcode, use dealer
        zipcode = this.dealerZipcode
      } else {
        // Try customer zipcode first
        tryDealerPostcode = true
      }
      const newProvince = await this.getProvinceFromPostcode(zipcode).catch(async () => {
        if (tryDealerPostcode) {
          return await this.getProvinceFromPostcode(this.dealerZipcode).catch(() => {
            return null
          })
        }
      })
      if (this.financialParams.province !== newProvince) {
        this.financialParams.province = newProvince
        this.requestFinancing()
      }
    },
    updateDiscount(selectedDiscount) {
      if (selectedDiscount.type === discountType.code) {
        if (selectedDiscount.selected) {
          this.financialParams.collectivityCode = selectedDiscount.code
        } else {
          this.financialParams.collectivityCode = null
        }
        this.requestFinancing()
      }
    },
    updateMultipleDiscounts(discounts) {
      this.financialParams.multipleDiscounts = discounts
      this.requestFinancing()
    },
    getSelectedProduct(type, value) {
      return this.productTypes[type].find((x) => x.value === value)
    },
    getSelectedProductByName(type, name) {
      return this.productTypes[type].find((x) => x.name == name)
    },
    retrieveText(labelName, params = null) {
      return this.i18n.t(`${this.labelPrefix}/${labelName}`, params)
    },
    async saveLease(isManualClick) {
      const request = this.getLeaseRequestParams()
      let leaseCallFunction = this.isPrivateLease
        ? FinancialServices.setPrivateLease
        : FinancialServices.setOperationalLease
      const response = await leaseCallFunction(request, true).catch((error) => {
        this.financialParams.leasePrice = null
        this.$store.dispatch(this.setLatestCallName, {
          responseCode: error.response?.status,
        })

        this.parseMessages(error.response?.data?.messages, error.response?.status)

        this.setFinancingIsLoading(false)
        return null
      })
      // 404 error
      if (!response) {
        return
      }
      this.quotationId = response?.quotationData?.quotationId
      if (isManualClick) {
        this.setPlQuotationId(this.quotationId)
      }
      this.leaseLink = response?.quotationData?.priceProposal?.quotationUrl
      let businessLocationBrandName =
        response?.quotationData?.priceProposal?.businessLocationBrandName
      let quotationUrlMessage = response?.quotationData?.priceProposal?.quotationUrlMessage
      this.leaseInfoText =
        (!!businessLocationBrandName ? `${businessLocationBrandName}. ` : '') +
        `${quotationUrlMessage}`
    },
    async applyForLease() {
      this.setFinancingIsLoading(true)
      await this.saveLease(true)
      this.openLeasePrompt(true)
      this.setFinancingIsLoading(false)
    },
    openLeaseLink() {
      window.open(this.leaseLink)
      this.openLeasePrompt(false)
    },
    openLeasePrompt(show) {
      this.showLeaseApply = show
      if (!open) {
        this.leaseLink = null
      }
    },
  },
}
</script>
